<template lang="pug">
	.outer-main
		background-particles
		main(
			ref="main"
			v-bind:class="mainClass"
			tabindex="0"
			@keydown.esc="onPressEsc"
			@keydown.enter="onPressEnter"
			@keydown.down="onPressDown"
			@keydown.up="onPressUp"
			@keydown.tab="onPressTab"
			@keydown.shift.tab="onPressShiftTab"
			@keydown.page-down="onPressPageDown"
			@keydown.page-up="onPressPageUp"
			@keydown.home="onPressHome"
			@keydown.end="onPressEnd"
		)
			.side-hero(@click="onClickHero")
				template(v-show="introDone")
					.hero-content
						portal-target(name="hero" slim :transition="fadeTransition")
					menu-button(:active="menuActive" @open="onOpen" @close="onClose")
					.hero-overlay
						logo
						div
							.hero-heading
								| Hukopedia
							.hero-subheading
								| The Huko Compendium
			.side-content
				.scrollable
					vue-scroll(ref="vs" :ops="ops")
						.pages
							.page.has-curl.has-border
								.curl
								slot

				nav(v-bind:class="activeMenuClass")
					.pages
						.page
							.page-scroll
								h2.title Table of Contents
								navigation(
									:active="menuActive"
									:route-area="routeArea"
									ref="nav"
									@close="onCloseNavigation"
								)
								.info-box.centered-box(v-if="!hasTouchScreen")
									.info-title Keyboard controls
									| The <i>arrow keys</i> move you up and down, with the <i>tab</i> key you can enter a submenu and <i>shift+tab</i> moves you back out. You can toggle this menu by pressing <i>esc</i>

			.side-content.cover(v-if="!openBook")
				.side-book-cover
					hard-cover(ref="cover" @opening="coverOpening")
</template>

<script>
import MenuButton from '@/components/MenuButton'
import HardCover from '@/components/HardCover'
import Navigation from '@/components/Navigation'
import BackgroundParticles from '@/components/BackgroundParticles'
import Logo from '@/components/Logo'
import { EventBus } from '@/helpers/event-bus'

export default {
	name      : 'BookLayout',
	components: {
		BackgroundParticles,
		MenuButton,
		HardCover,
		Navigation,
		Logo
	},
	data () {
		return {
			routeArea    : '',
			routeUrl     : '',
			menuActive   : false,
			openBook     : false,
			timeoutScroll: null,
			ops          : {
				vuescroll  : {},
				scrollPanel: {
					initialScrollY: 0,
					speed         : 300,
					scrollingX    : false,
					scrollingY    : false,
					easing        : 'easeInOutCubic'
				},
				rail: {
					background  : 'rgba(0,0,0,0)',
					opacity     : 0.0,
					border      : 'none',
					size        : '12px',
					gutterOfEnds: '6px',
					gutterOfSide: '6px',
					keepShow    : false
				},
				bar: {
					onlyShowBarOnScroll: true,
					keepShow           : false,
					background         : '#9e34d9'
				}
			},
			timeoutHandlerCover : null,
			timeoutHandlerScroll: null,
			timeoutHandlerRoute : null
		}
	},
	computed: {
		activeMenuClass () {
			return { 'is-active': this.menuActive }
		},
		mainClass () {
			return {
				'is-intro'   : !this.introDone,
				'menu-active': this.menuActive
			}
		},
		hasTouchScreen () {
			return 'ontouchstart' in window || navigator.msMaxTouchPoints
		},
		introDone: {
			get () {
				return this.$store.state.general.intro
			},
			set (val) {
				this.$store.commit('general/FIRST_RUN_INTRO', val)
			}
		},
		fadeTransition () {
			return {
				functional: true,
				render (h, context) {
					return h('transition', { props: { name: 'fade', mode: 'out-in' } }, context.children)
				}
			}
		}
	},
	mounted () {
		if (process.env.NODE_ENV === 'development') {
			this.introDone = false
			this.openBook = false
		}

		EventBus.$on('menu-open', this.onOpen)

		this.$nextTick(() => {
			if (this.introDone) {
				this.openBook = true
			}

			this.routeArea = this.$route.meta ? this.$route.meta.area : ''
			this.routeUrl = this.$route.path

			this.$refs.main.focus()
		})
	},
	beforeDestroy () {
		clearTimeout(this.timeoutHandlerScroll)
		clearTimeout(this.timeoutHandlerCover)
		clearTimeout(this.timeoutHandlerRoute)
	},
	methods: {
		onOpen () {
			this.menuActive = true
		},
		onClose () {
			this.menuActive = false
		},
		onCloseNavigation () {
			this.$refs.vs.scrollTo({ y: 0 }, 500, 'easeInOutCubic')
			this.$refs.main.focus()
			this.onClose()
		},
		onClickHero () {
			if (this.menuActive) this.onClose()
		},
		onPressEsc (e) {
			if (this.openBook) {
				this.menuActive = !this.menuActive
				e.preventDefault()
			}
			if (!this.introDone && this.$refs.cover) {
				this.$refs.cover.onClick()
			}
		},
		onPressEnter () {
			if (this.menuActive) {
				this.$refs.nav.submitSelection()
			}
			if (!this.introDone && this.$refs.cover) {
				this.$refs.cover.onClick()
			}
		},
		onPressTab (e) {
			if (this.menuActive) {
				this.$refs.nav.enterSub()
				e.preventDefault()
			}
		},
		onPressShiftTab (e) {
			if (this.menuActive) {
				this.$refs.nav.exitSub()
				e.preventDefault()
			}
		},
		onPressDown () {
			if (this.menuActive) {
				this.$refs.nav.increment()
			} else if (this.openBook) {
				this.scrollDown()
			}
		},
		onPressUp () {
			if (this.menuActive) {
				this.$refs.nav.decrement()
			} else if (this.openBook) {
				this.scrollUp()
			}
		},
		onPressPageUp (e) {
			if (this.openBook && !this.menuActive) {
				this.scrollUp()
			}
			if (this.menuActive) {
				this.onPressUp()
				e.preventDefault()
			}
		},
		onPressPageDown (e) {
			if (this.openBook && !this.menuActive) {
				this.scrollDown()
			}
			if (this.menuActive) {
				this.onPressDown()
				e.preventDefault()
			}
		},
		onPressHome () {
			if (this.openBook && !this.menuActive) {
				this.$refs.vs.scrollTo({ y: 0 }, 500, 'easeInOutCubic')
			}
		},
		onPressEnd () {
			if (this.openBook && !this.menuActive) {
				this.$refs.vs.scrollTo({ y: '100%' }, 500, 'easeInOutCubic')
			}
		},
		scrollUp () {
			if (this.timeoutScroll) return
			this.timeoutScroll = true
			this.$refs.vs.scrollBy({ dy: -200 }, 500, 'easeInOutCubic')
			this.timeoutHandlerScroll = setTimeout(() => {
				this.timeoutScroll = false
			}, 500)
		},
		scrollDown () {
			if (this.timeoutScroll) return
			this.timeoutScroll = true
			this.$refs.vs.scrollBy({ dy: 200 }, 500, 'easeInOutCubic')
			this.timeoutHandlerScroll = setTimeout(() => {
				this.timeoutScroll = false
			}, 500)
		},
		coverOpening () {
			this.timeoutHandlerCover = setTimeout(() => {
				this.introDone = true
				this.coverOpened()
			}, 1200)
		},
		coverOpened () {
			this.$refs.main.focus()
			this.timeoutHandlerCover = setTimeout(() => {
				this.openBook = true
			}, 1000)
		},
		updateOps () {
			const showBar = !this.menuActive && this.openBook
			this.ops.scrollPanel.scrollingY = showBar
			this.ops.rail.opacity = showBar ? 0.5 : 0
			// this.ops.rail.keepShow = showBar
			this.ops.bar.onlyShowBarOnScroll = !showBar
			// this.ops.bar.keepShow = showBar
			// this.$refs.vs.refresh()
		}
	},
	watch: {
		menuActive () {
			this.updateOps()
		},
		openBook () {
			this.updateOps()
		},
		'$route' (val) {
			this.$nextTick(() => {
				this.$refs.vs.scrollTo({ y: 0 }, 500, 'easeInOutCubic')

				this.timeoutHandlerRoute = setTimeout(() => {
					this.routeArea = this.$route.meta ? this.$route.meta.area : ''
					this.routeUrl = this.$route.path
					this.timeoutScroll = false

					if (this.openBook && !this.menuActive) {
						this.$refs.main.focus()
					}
				}, 500)
			})
		}
	}
}
</script>
